import { $createLinkNode, $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';

import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  BaseSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import * as styles from './FloatingLinkMenuPlugin.css';
import * as toolbarStyles from '../FloatingMenuPlugin/FloatingMenu.css';
import { isValidUrl, sanitizeUrl } from '@mutiny-pkg/bin-ui/src/utils/url';
import { CLOSE_COLOR_MENU, CLOSE_LINK_EDITOR } from '@mutiny-pkg/bin-ui/src/richtext/RichInput/lexicalConfig';

import { Flex } from '@mutiny-pkg/dumpster-ui/components/Flex';
import { ButtonGroup } from '@mutiny-pkg/dumpster-ui/components/ButtonGroup';
import { Button, IconButton } from '@mutiny-pkg/dumpster-ui/components/Button';
import { Message } from '@mutiny-pkg/dumpster-ui/components/Message/Message';
import { Icons } from '@mutiny-pkg/dumpster-ui/icons';

export const FloatingLinkEditorPlugin = React.forwardRef<HTMLDivElement, { editor: LexicalEditor; active: boolean }>(
  ({ editor, active }, ref) => {
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const editorRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [linkUrl, setLinkUrl] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [lastSelection, setLastSelection] = useState<BaseSelection | null>(null);
    const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);
    const [prevElement, setPrevElement] = useState<HTMLElement | null>(null);

    const $updateLinkEditor = useCallback(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const node = anchorNode.getParent();
        const linkParent = $findMatchingParent(anchorNode, $isLinkNode);
        let element =
          anchorNode.getKey() === 'root'
            ? anchorNode
            : $findMatchingParent(anchorNode, (e) => {
                return $isElementNode(e);
              });

        if (element === null) {
          element = anchorNode.getTopLevelElementOrThrow();
        }

        const elementKey = element.getKey();
        const elementDOM = editor.getElementByKey(elementKey);
        if (elementDOM !== null && elementDOM !== prevElement) {
          setIsLinkEditMode(false);
        }
        setPrevElement(elementDOM);
        if (linkParent) {
          setLinkUrl(linkParent.getURL());
        } else if ($isLinkNode(node)) {
          setLinkUrl(node.getURL());
        } else {
          setLinkUrl('');
        }
        setIsDirty(false);
      }
      const editorElem = editorRef.current;
      const nativeSelection = editor._window?.getSelection();

      if (editorElem === null) {
        return true;
      }

      const rootElement = editor.getRootElement();

      if (
        selection !== null &&
        nativeSelection &&
        rootElement !== null &&
        rootElement.contains(nativeSelection.anchorNode) &&
        editor.isEditable()
      ) {
        setLastSelection(selection);
      }
      return true;
    }, [editor, prevElement]);

    useEffect(() => {
      return mergeRegister(
        editor.registerUpdateListener(({ editorState }) => {
          editorState.read(() => {
            $updateLinkEditor();
          });
        }),

        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            $updateLinkEditor();
            return true;
          },
          COMMAND_PRIORITY_LOW,
        ),

        editor.registerCommand(
          CLOSE_LINK_EDITOR,
          () => {
            setIsLinkEditMode(false);
            return false;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
      );
    }, [editor, $updateLinkEditor]);

    useEffect(() => {
      inputRef?.current?.focus();
    }, [isLinkEditMode]);

    const handleLinkSubmission = useCallback(
      (urlValue: string) => {
        if (urlValue === '') {
          setIsLinkEditMode(false);
          return;
        }

        if (lastSelection === null || !isValidUrl(urlValue, { allowAnyProtocol: true })) return;

        editor.update(() => {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(urlValue));
          const selection = lastSelection;
          if ($isRangeSelection(selection)) {
            const parent = selection.anchor.getNode().getParent();
            if ($isAutoLinkNode(parent)) {
              const linkNode = $createLinkNode(parent.getURL(), {
                rel: parent.__rel,
                target: parent.__target,
                title: parent.__title,
              });
              parent.replace(linkNode, true);
            }
          }
          setIsLinkEditMode(false);
        });
      },
      [editor, lastSelection],
    );

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setLinkUrl(event.target.value);
      setIsDirty(true);
    };

    useEffect(() => {
      const handleEditorBlur = (event: FocusEvent) => {
        if (!isDirty && !editorRef.current?.contains(event.relatedTarget as Node)) {
          setLinkUrl('');
          setIsLinkEditMode(false);
          editor.dispatchCommand(BLUR_COMMAND, event);
        }
      };
      const editorElem = editorRef.current;

      editorElem?.addEventListener('blur', handleEditorBlur, true);
      return () => {
        editorElem?.removeEventListener('blur', handleEditorBlur, true);
      };
    }, [editor, isDirty]);

    const monitorInputInteraction = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleLinkSubmission(linkUrl);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setIsLinkEditMode(false);
      }
    };

    const removeLink = () => {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      setLinkUrl('');
      setIsLinkEditMode(false);
    };

    const toggleLinkEditMode = useCallback(() => {
      if (!isLinkEditMode) {
        editor.dispatchCommand(CLOSE_COLOR_MENU, true);
      }
      setIsLinkEditMode(!isLinkEditMode);
    }, [editor, setIsLinkEditMode, isLinkEditMode]);

    return (
      <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
        <IconButton
          ref={buttonRef}
          tabIndex={-1}
          className={toolbarStyles.toolbarButton({ active })}
          label="Link"
          icon={Icons.Link}
          variant="text"
          onMouseDown={(event) => {
            event.preventDefault();
            toggleLinkEditMode();
          }}
        />
        <div
          ref={editorRef}
          style={{
            position: 'absolute',
            top: 30,
            zIndex: 1000,
          }}
          className="link-editor"
          data-link-menu-open={isLinkEditMode}
        >
          {isLinkEditMode && (
            <Flex.Container className={styles.container} flexDirection="column">
              <Flex.Container className={styles.content} gap="4u" flexDirection="column">
                Add a URL
                <input
                  ref={inputRef}
                  className={styles.input}
                  value={linkUrl}
                  onChange={handleInputChange}
                  onKeyDown={(event) => {
                    monitorInputInteraction(event);
                  }}
                  placeholder="Insert a link, then hit Enter"
                />
                <Message variant="error" active={Boolean(linkUrl) && !isValidUrl(linkUrl, { allowAnyProtocol: true })}>
                  Please enter a valid URL
                </Message>
                {/* uncomment TextInput when we migrate lodash to lodash-es in dumpster */}
                {/* <TextInput
              error={Boolean(editedLinkUrl) && !isValidUrl(editedLinkUrl)}
              errorMessage="Please enter a valid URL"
              fullWidth
              ref={inputRef}
              value={editedLinkUrl}
              onChange={(event) => {
                setEditedLinkUrl(event.target.value);
              }}
              onKeyDown={(event) => {
                monitorInputInteraction(event);
              }}
              placeholder="Paste a link, then hit Enter"
            /> */}
              </Flex.Container>
              <Flex.Container justifyContent="center" alignItems="center">
                <ButtonGroup orientation="inline" buttonGap="none" fullWidth>
                  <Button
                    variant="secondary"
                    size="large"
                    tabIndex={-1}
                    onClick={removeLink}
                    onMouseDown={(e) => e.preventDefault()}
                    fullWidth
                  >
                    Remove link
                  </Button>
                  <Button
                    disabled={!linkUrl}
                    variant="primary"
                    size="large"
                    tabIndex={-1}
                    onClick={() => handleLinkSubmission(linkUrl)}
                    onMouseDown={(e) => e.preventDefault()}
                    fullWidth
                  >
                    Save
                  </Button>
                </ButtonGroup>
              </Flex.Container>
            </Flex.Container>
          )}
        </div>
      </div>
    );
  },
);

FloatingLinkEditorPlugin.displayName = 'FloatingLinkEditorPlugin';
